/**
 * SB6-AUTH-RECONNECT-01 — proactive token-refresh hook.
 *
 * Christian's verbatim ask: "if a session times out, while the user is
 * working - we need to auto reconnect, so we need to create new session
 * in the background automatically." A reactive refresh-on-401 (the SB6
 * AUTH-REFRESH-01 path) still produces a brief moment where the FE has
 * sent a request with a dead token; on a slow link that surfaces as a
 * stutter. We close that window by firing the refresh BEFORE the
 * deadline.
 *
 * Behaviour:
 *   - On mount, subscribe to `accessTokenExpiresAt` from the auth store.
 *   - Whenever it changes, schedule `tryRefresh()` to fire at
 *     `expiresAt - now - REFRESH_SAFETY_MARGIN_MS`.
 *   - If the deadline is already past (or within the safety margin), fire
 *     immediately.
 *   - The single-in-flight dedupe inside `tryRefresh` guarantees a
 *     concurrent reactive 401-refresh won't double up.
 *   - On unmount, cancel the pending timer.
 *
 * Mount this hook ONCE at the app shell (`AppChrome` / `AppShell`). Mounting
 * it inside RequireAuth keeps the timer scoped to authenticated routes —
 * we don't want a refresh firing on /login.
 */

import { useEffect } from "react";
import { useAuthStore } from "./store.js";
import { tryRefresh } from "./fetchWithRefresh.js";
import { REFRESH_SAFETY_MARGIN_MS } from "./sessionLimits.js";
import { isPopoutWindow } from "./windowRole.js";

export function useProactiveTokenRefresh(): void {
  const expiresAt = useAuthStore((s) => s.session?.accessTokenExpiresAt);
  const sessionPresent = useAuthStore((s) => s.session !== null);

  useEffect(() => {
    if (!sessionPresent || typeof expiresAt !== "number") return;
    // ANA-R1-09 — a pop-out follower must NOT run its own proactive timer.
    // Two windows refreshing the SAME refresh token race: each Zitadel
    // exchange rotates the token, so the loser's copy is invalidated and it
    // gets bounced to /login → a second session. The main window is the sole
    // refresh authority; the pop-out receives rotated tokens over the auth
    // BroadcastChannel (see authBroadcast.ts) and stays current without ever
    // touching Zitadel itself.
    if (isPopoutWindow()) return;

    const now = Date.now();
    const fireAt = expiresAt - REFRESH_SAFETY_MARGIN_MS;
    const delay = Math.max(0, fireAt - now);

    const timer = setTimeout(() => {
      void tryRefresh();
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [expiresAt, sessionPresent]);
}
